import { XMarkIcon } from "@heroicons/react/24/outline";
import { InfoWithLabel } from "@/core/components/info-with-label";
import { Button } from "@/core/components/ui/button";
import { Skeleton } from "@/core/components/ui/skeleton";
import { Stack } from "@/core/components/ui/stack";
import { toast } from "@/core/components/ui/toast";
import { Typography } from "@/core/components/ui/typography";
import { BusinessSettingsSidebarWrapper } from "@/business-settings/common/business-settings-sidebar-wrapper";
import { formatDateAndTime } from "@/core/utils/date";
import { formatMoney } from "@/core/utils/money";
import { useSpiffSidebar } from "@/spiffs/context/spiff-sidebar-context";
import {
  useBulkApproveSpiffs,
  useDismissSpiff,
  useGetSpiff,
  useReinstateSpiff,
} from "@/spiffs/query";
import { PhotoTrigger } from "@/spiffs/spiffs.d";
import { useGetBusinessUser } from "@/user/query";
import { SpiffEventBadge } from "./spiff-event-badge";

interface SpiffPhotoSidebarProps {
  spiffId: string;
  businessId?: string | null;
}

export const SpiffPhotoSidebar = ({
  spiffId,
  businessId,
}: SpiffPhotoSidebarProps) => {
  const { resetSidebar } = useSpiffSidebar();
  const { data: spiff, isLoading: isLoadingSpiff } = useGetSpiff(
    businessId,
    spiffId,
  );
  const { data: spiffUser, isLoading: isLoadingSpiffUser } = useGetBusinessUser(
    businessId,
    spiff?.user_id,
  );

  const { mutate: approveSpiff, isPending: isApproving } =
    useBulkApproveSpiffs(businessId);
  const { mutate: dismissSpiff, isPending: isDismissing } =
    useDismissSpiff(businessId);
  const { mutate: reinstateSpiff, isPending: isReinstating } =
    useReinstateSpiff(businessId);

  const handleApprove = () => {
    approveSpiff(
      { spiffIds: [spiffId] },
      {
        onSuccess: () => {
          toast.success("Spiff approved successfully");
          resetSidebar();
        },
        onError: () => {
          toast.error("Failed to approve spiff");
        },
      },
    );
  };

  const handleDismiss = () => {
    dismissSpiff(
      { spiffIds: [spiffId] },
      {
        onSuccess: () => {
          toast.success("Spiff dismissed successfully");
          resetSidebar();
        },
        onError: () => {
          toast.error("Failed to dismiss spiff");
        },
      },
    );
  };

  const handleUndismiss = () => {
    reinstateSpiff(
      { spiffId },
      {
        onSuccess: () => {
          toast.success("Spiff reinstated successfully");
          resetSidebar();
        },
        onError: () => {
          toast.error("Failed to reinstate spiff");
        },
      },
    );
  };

  if (isLoadingSpiff || !spiff || isLoadingSpiffUser) {
    return (
      <BusinessSettingsSidebarWrapper>
        <Stack
          direction="row"
          justify="between"
          fullWidth
          className="bg-bg-secondary/50 p-6"
        >
          <Stack>
            <Skeleton className="h-6 w-24" />
            <Skeleton className="h-5 w-32" />
          </Stack>
          <Button variant="unstyled">
            <XMarkIcon className="size-6 text-secondary-text" />
          </Button>
        </Stack>
        <div className="w-full flex-1 overflow-auto px-6 py-4">
          <Stack gap="md" fullWidth>
            <Stack direction="row" gap="none" fullWidth>
              <InfoWithLabel label="Technician">
                <Skeleton className="h-5 w-32" />
              </InfoWithLabel>
              <InfoWithLabel label="Amount">
                <Skeleton className="h-5 w-20" />
              </InfoWithLabel>
            </Stack>
            <InfoWithLabel label="Submitted at">
              <Skeleton className="h-5 w-40" />
            </InfoWithLabel>
            <InfoWithLabel label="Photo">
              <Skeleton className="h-48 w-full rounded-lg" />
            </InfoWithLabel>
          </Stack>
        </div>
      </BusinessSettingsSidebarWrapper>
    );
  }

  const photoTrigger = spiff.trigger as PhotoTrigger;

  return (
    <BusinessSettingsSidebarWrapper>
      <Stack fullWidth className="bg-bg-secondary/50 p-6">
        <Stack direction="row" justify="between" fullWidth>
          <Stack>
            <SpiffEventBadge triggerEvent={spiff.trigger_event} />
            <Typography variant="subtitleW1">Payment Due</Typography>
          </Stack>
          <Button variant="unstyled" onClick={resetSidebar}>
            <XMarkIcon className="size-6 text-secondary-text" />
          </Button>
        </Stack>
        {spiff.dismissed_at ? (
          <Button
            onClick={handleUndismiss}
            loading={isReinstating}
            fullWidth
            size="sm"
          >
            Undismiss
          </Button>
        ) : (
          <Stack direction="row" justify="between" fullWidth>
            <Button
              onClick={handleApprove}
              loading={isApproving}
              disabled={isDismissing}
              className="flex-1"
              size="sm"
            >
              Approve
            </Button>
            <Button
              variant="secondary"
              onClick={handleDismiss}
              loading={isDismissing}
              disabled={isApproving}
              className="flex-1"
              size="sm"
            >
              Dismiss
            </Button>
          </Stack>
        )}
      </Stack>
      <div className="w-full flex-1 overflow-auto px-6 py-4">
        <Stack gap="md" fullWidth>
          <Stack direction="row" gap="none" fullWidth>
            <InfoWithLabel label="Technician">
              {spiffUser?.display_identification}
            </InfoWithLabel>
            <InfoWithLabel label="Amount">
              {formatMoney(spiff.amount)}
            </InfoWithLabel>
          </Stack>

          <InfoWithLabel label="Submitted at">
            {formatDateAndTime(photoTrigger?.created_at || spiff.created_at)}
          </InfoWithLabel>

          <InfoWithLabel label="Photo">
            {photoTrigger?.photo_url ? (
              <img
                src={photoTrigger.photo_url}
                alt="Yard sign"
                className="w-full rounded-lg border border-stroke-tertiary"
              />
            ) : (
              <Typography variant="bodyW1" textColor="secondary">
                No photo available
              </Typography>
            )}
          </InfoWithLabel>
        </Stack>
      </div>
    </BusinessSettingsSidebarWrapper>
  );
};
